/**
 * 对图片的代理，在图片加载完毕之前，展示回退图片
 */

import React, { FC, MouseEvent, CSSProperties, memo, useState, useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { useFlowEffect } from '@@/react-hooks';
import { loadImage } from '@@/utils';
import styles from './index.module.less';

interface ImgCoreProps {
  zoomable?: boolean;
  src: string;
  alt?: string;
  className?: string;
  style?: CSSProperties;
  onClick?(e: MouseEvent): void;
}

const BackgroundModeImg: FC<ImgCoreProps> = memo(({ src, onClick, className, style, zoomable, children }) => {
  const combinedStyle = {
    ...(style || {}),
    backgroundImage: `url(${src})`,
  };
  return (
    <span
      className={classnames(className, styles['background-mode-img'], {
        [styles.img_zoomable]: zoomable,
      })}
      style={combinedStyle}
      onClick={onClick}
    >
      {children}
    </span>
  );
});

const NormalModeImg: FC<ImgCoreProps> = memo(props => {
  const { src, className = '', zoomable, children, ...rest } = props;

  const imageState = useFlowEffect(
    null,
    function* ({ put, call, cancel }) {
      if (!src) return;
      yield cancel();
      const imageDom = yield call(loadImage, src);
      yield put(imageDom);
    },
    [src]
  );

  if (imageState.loading) {
    if (React.Children.count(children)) {
      return <>{children}</>;
    }
    return <span className={classnames(className, styles.hint)}>图片加载中</span>;
  }

  if (imageState.error) {
    return <span className={classnames(className, styles.hint)}>图片加载失败</span>;
  }

  if (!src) {
    return <span className={classnames(className, styles.no_image)}>没有图片</span>;
  }

  return (
    <img
      className={classnames(className, {
        [styles.img_zoomable]: zoomable,
      })}
      src={src}
      alt="with-fallback"
      {...rest}
    />
  );
});

export interface ImgProps extends Omit<ImgCoreProps, 'src'> {
  src: string | Blob;
  backgroundMode?: boolean;
}

const blobWeakMap = new WeakMap<Blob, string>();

export const Img: FC<ImgProps> = memo(({ backgroundMode, onClick, zoomable, src, ...rest }) => {
  const [zoom, setZoom] = useState(false);

  const onClickImage = useCallback(
    (e: MouseEvent) => {
      if (zoomable) {
        setZoom(true);
      }
      if (onClick) {
        onClick(e);
      }
    },
    [onClick]
  );

  const stringSrc = useMemo(() => {
    if (src instanceof Blob) {
      const cachedObjectURL = blobWeakMap.get(src);
      if (cachedObjectURL) {
        return cachedObjectURL;
      }
      const objectURL = URL.createObjectURL(src);
      blobWeakMap.set(src, objectURL);
      return objectURL;
    }
    return src;
  }, [src]);

  const props: ImgCoreProps = {
    onClick: onClickImage,
    src: stringSrc,
    zoomable,
    ...rest,
  };

  return backgroundMode ? <BackgroundModeImg zoomable {...props} /> : <NormalModeImg zoomable {...props} />;
});
